"use client"

import type React from "react"

import { useState } from "react"
import { motion } from "framer-motion"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Loader2, ArrowLeft } from "lucide-react"
import { useAuth } from "./auth-context"

type SignupScreenProps = {
  setActiveScreen: (screen: string) => void
  onBackClick?: () => void
}

export function SignupScreen({ setActiveScreen, onBackClick }: SignupScreenProps) {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [errors, setErrors] = useState<{
    name?: string
    email?: string
    password?: string
    confirmPassword?: string
    terms?: string
  }>({})

  const { signup, isLoading } = useAuth()

  const validateForm = () => {
    const newErrors: {
      name?: string
      email?: string
      password?: string
      confirmPassword?: string
      terms?: string
    } = {}

    if (!name.trim()) {
      newErrors.name = "Name is required"
    }

    if (!email) {
      newErrors.email = "Email is required"
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email is invalid"
    }

    if (!password) {
      newErrors.password = "Password is required"
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters"
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match"
    }

    if (!acceptTerms) {
      newErrors.terms = "You must accept the terms and conditions"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    try {
      await signup(name, email, password)
      setActiveScreen("analyze")
    } catch (error) {
      console.error("Signup failed:", error)
    }
  }

  return (
    <div className="flex flex-col h-full p-5 pt-8">
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-4">
        <div className="flex items-center mb-4">
          <Button
            variant="ghost"
            size="icon"
            className="mr-2 text-zinc-400 hover:text-white hover:bg-white/10"
            onClick={onBackClick || (() => setActiveScreen("login"))}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-light">
            <span className="gradient-text font-medium">Create Account</span>
          </h1>
        </div>
        <p className="text-zinc-400 text-sm">Join Red Flag Filter and never send a regrettable message again</p>
      </motion.div>

      <motion.form
        onSubmit={handleSubmit}
        className="space-y-5"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-light">
            Full Name
          </Label>
          <Input
            id="name"
            type="text"
            placeholder="Your Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="glass rounded-xl text-white border-none focus-visible:ring-1 focus-visible:ring-rose-500/50 h-12"
          />
          {errors.name && <p className="text-xs text-rose-400 mt-1">{errors.name}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-light">
            Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="glass rounded-xl text-white border-none focus-visible:ring-1 focus-visible:ring-rose-500/50 h-12"
          />
          {errors.email && <p className="text-xs text-rose-400 mt-1">{errors.email}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password" className="text-sm font-light">
            Password
          </Label>
          <Input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="glass rounded-xl text-white border-none focus-visible:ring-1 focus-visible:ring-rose-500/50 h-12"
          />
          {errors.password && <p className="text-xs text-rose-400 mt-1">{errors.password}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword" className="text-sm font-light">
            Confirm Password
          </Label>
          <Input
            id="confirmPassword"
            type="password"
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="glass rounded-xl text-white border-none focus-visible:ring-1 focus-visible:ring-rose-500/50 h-12"
          />
          {errors.confirmPassword && <p className="text-xs text-rose-400 mt-1">{errors.confirmPassword}</p>}
        </div>

        <div className="flex items-start space-x-2">
          <Checkbox
            id="terms"
            checked={acceptTerms}
            onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
            className="data-[state=checked]:bg-rose-500 data-[state=checked]:border-rose-500"
          />
          <div className="grid gap-1.5 leading-none">
            <label htmlFor="terms" className="text-sm font-light text-zinc-300 leading-tight cursor-pointer">
              I accept the <span className="text-rose-400">Terms of Service</span> and{" "}
              <span className="text-rose-400">Privacy Policy</span>
            </label>
            {errors.terms && <p className="text-xs text-rose-400">{errors.terms}</p>}
          </div>
        </div>

        <Button
          type="submit"
          disabled={isLoading}
          className="w-full gradient-bg text-white hover:opacity-90 rounded-xl h-12 font-light mt-4"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Creating Account...
            </>
          ) : (
            "Create Account"
          )}
        </Button>
      </motion.form>

      <motion.div
        className="mt-6 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <p className="text-zinc-400 text-sm">
          Already have an account?{" "}
          <button onClick={() => setActiveScreen("login")} className="text-rose-400 hover:text-rose-300">
            Sign in
          </button>
        </p>
      </motion.div>
    </div>
  )
}

